import type { Collection } from '../../features/products/api/metaDataApi'

/**
 * Presentation helpers for collection-level discounts (PE-2135).
 *
 * The one trap worth guarding: product-service stores a FLAT `discountValue` in
 * MINOR UNITS (kobo), while PERCENTAGE is a plain 0-100 number. Formatting the
 * two the same way inflates a flat discount 100x.
 */

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null

export const hasDiscount = (c: Pick<Collection, 'discountType' | 'discountValue'>): boolean =>
  !!c.discountType && c.discountValue != null

/** "20% off" | "₦1,500.00 off" | "—" */
export const describeDiscount = (c: Pick<Collection, 'discountType' | 'discountValue'>): string => {
  if (!hasDiscount(c)) return '—'
  if (c.discountType === 'PERCENTAGE') return `${c.discountValue}% off`
  const naira = (c.discountValue as number) / 100
  return `₦${naira.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} off`
}

/** The window the discount runs for, or null when it is always-on. */
export const describeWindow = (c: Pick<Collection, 'discountStartsAt' | 'discountEndsAt'>): string | null => {
  const from = formatDate(c.discountStartsAt)
  const to = formatDate(c.discountEndsAt)
  if (from && to) return `${from} – ${to}`
  if (from) return `from ${from}`
  if (to) return `until ${to}`
  return null
}

export type DiscountState = 'active' | 'scheduled' | 'ended'

export const discountState = (
  c: Pick<Collection, 'discountStartsAt' | 'discountEndsAt'>,
  now: Date = new Date(),
): DiscountState => {
  if (c.discountEndsAt && new Date(c.discountEndsAt) < now) return 'ended'
  if (c.discountStartsAt && new Date(c.discountStartsAt) > now) return 'scheduled'
  return 'active'
}
